import { Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { DialogPlanLlmPrompt, DialogPlanLlmPromptApi, DialogPlanLlmPromptSpeakNoGrade } from "../../api/DialogPlanLlmPromptApi";
import AppBarContext from '../../context/AppBarContext';
import { handleErrorMessage } from '../ErrorDialog';

interface DialogPlanLlmPromptSpeakNoGradeFormProps {
    dialogPlanLlmPromptId?: number;
    dialogPlanLlmPrompt?: DialogPlanLlmPromptSpeakNoGrade;
    setDialogPlanLlmPrompt?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt) => void;
    handleLlmPromptChange?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt) => void;
    handleRegenerateDialogPlanLlmPrompt?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt, fieldName: string) => void;
    isRegenerating?: boolean;

    id: number;
    dialogPlanId: number;
    orderIndex: number;
    isTeacher: boolean;
    prompt: string;
    isNoteToStudent: boolean;
    yesResponse: string | null;
    imageSource: string | null;
}

export const DialogPlanLlmPromptSpeakNoGradeForm: React.FC<DialogPlanLlmPromptSpeakNoGradeFormProps> = ({
    dialogPlanLlmPromptId, dialogPlanLlmPrompt, setDialogPlanLlmPrompt, handleLlmPromptChange, handleRegenerateDialogPlanLlmPrompt, isRegenerating,
    id, dialogPlanId, orderIndex, isTeacher, prompt, isNoteToStudent, yesResponse, imageSource }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [teacherChinesePrompt, setTeacherChinesePrompt] = React.useState("");
    const [studentResponsePrompt, setStudentResponsePrompt] = React.useState("<Answer/>");
    const handleTeacherChinesePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => { setTeacherChinesePrompt(e.target.value); };
    const handleStudentResponsePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => { setStudentResponsePrompt(e.target.value); };

    const hasLoadedDialogPlanLlmPromptFields = React.useRef(false);

    const setDialogPlanLlmPromptFields = React.useCallback((dialogPlanLlmPrompt: DialogPlanLlmPromptSpeakNoGrade) => {
        if (dialogPlanLlmPrompt.class_name == "DialogPlanLlmPromptSpeakNoGradeModel") {
            setTeacherChinesePrompt(dialogPlanLlmPrompt.teacher_chinese_prompt ? dialogPlanLlmPrompt.teacher_chinese_prompt : "")
            setStudentResponsePrompt(dialogPlanLlmPrompt.student_response_prompt ? dialogPlanLlmPrompt.student_response_prompt : "")
            hasLoadedDialogPlanLlmPromptFields.current = true;
        }
    }, []);

    React.useEffect(() => {
        if (!dialogPlanLlmPromptId && dialogPlanLlmPrompt) {
            setDialogPlanLlmPromptFields(dialogPlanLlmPrompt);
        }
    }, [dialogPlanLlmPrompt, dialogPlanLlmPromptId, setDialogPlanLlmPromptFields])
    React.useEffect(() => {
        if (dialogPlanLlmPromptId && !dialogPlanLlmPrompt) {
            DialogPlanLlmPromptApi.getById(dialogPlanLlmPromptId)
                .then((response) => {
                    const dialogPlanLlmPrompt = response.data as DialogPlanLlmPromptSpeakNoGrade;
                    setDialogPlanLlmPromptFields(dialogPlanLlmPrompt);
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        }
    }, [dialogPlanLlmPromptId, dialogPlanLlmPrompt, history, location, setErrorMessage, setDialogPlanLlmPromptFields])

    const effectiveTeacherChinesePrompt = hasLoadedDialogPlanLlmPromptFields.current
        ? teacherChinesePrompt
        : dialogPlanLlmPrompt?.teacher_chinese_prompt ?? teacherChinesePrompt;
    const effectiveStudentResponsePrompt = hasLoadedDialogPlanLlmPromptFields.current
        ? studentResponsePrompt
        : dialogPlanLlmPrompt?.student_response_prompt ?? studentResponsePrompt;

    const currentDialogPlanLlmPrompt = React.useMemo(() => (
        new DialogPlanLlmPromptSpeakNoGrade(
            id,
            dialogPlanId,
            orderIndex,
            isTeacher,
            prompt,
            isNoteToStudent,
            yesResponse as string | "ERROR",
            imageSource,
            effectiveTeacherChinesePrompt,
            [], // TODO pinyin
            effectiveStudentResponsePrompt,
        )
    ), [
        id, dialogPlanId, orderIndex, isTeacher, prompt, isNoteToStudent, yesResponse, imageSource,
        effectiveTeacherChinesePrompt, effectiveStudentResponsePrompt,
    ]);

    React.useEffect(() => {
        if (handleLlmPromptChange) {
            handleLlmPromptChange(currentDialogPlanLlmPrompt);
        }
        if (setDialogPlanLlmPrompt) {
            setDialogPlanLlmPrompt(currentDialogPlanLlmPrompt);
        }
    }, [currentDialogPlanLlmPrompt, handleLlmPromptChange, setDialogPlanLlmPrompt])

    return (
        <Stack spacing={4} sx={{
            width: dialogPlanLlmPrompt ? '100%' : '90%', // Conditionally set width
            margin: '0 auto', pt: 2, pb: 4
        }}>

            <TextField
                label="Llm instructions for Teacher Chinese Prompt"
                value={teacherChinesePrompt}
                onChange={handleTeacherChinesePromptChange}
                multiline
                rows={5}
            />
            {/* hidden spacer field */}
            <TextField
                label="Teacher Chinese Prompt Pinyin"
                sx={{ opacity: 0 }}
            // value={teacherChinesePromptPinyin}
            // onChange={handleTeacherChinesePromptPinyinChange}
            // required
            />
            {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button variant="contained"
                        onClick={() => handleRegenerateDialogPlanLlmPrompt(currentDialogPlanLlmPrompt, "teacher_chinese_prompt")}
                        disabled={isRegenerating}>
                        Regenerate
                        {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                    </Button>
                </Box>
            </>}
            <div>
                <TextField
                    label="Llm instructions for Student Chinese Prompt"
                    value={studentResponsePrompt ? studentResponsePrompt : "<Answer/>"}
                    onChange={handleStudentResponsePromptChange}
                    required
                    sx={{ width: '100%' }}
                    multiline
                    rows={5}
                />
                <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                    Use &lt;Answer/&gt; to mark where the response is inserted
                </Typography>
                {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button variant="contained"
                            onClick={() => handleRegenerateDialogPlanLlmPrompt(currentDialogPlanLlmPrompt, "student_response_prompt")}
                            disabled={isRegenerating}>
                            Regenerate
                            {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                        </Button>
                    </Box>
                </>}
            </div>
        </Stack>
    )
}
